"use client"

import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { DialogShell } from "@/components/graphite/dialog-shell"
import { FIELD_UNDERLINE, FieldShell } from "@/components/graphite/field"
import { PageAction } from "@/components/graphite/page-header"
import { QuietAction } from "@/components/graphite/surface"
import { stageHex, stageTint, TINT } from "@/lib/stage-colors"
import { cn } from "@/lib/utils"
import { Send, CheckCircle } from "lucide-react"
import { getGetEmailsQueryKey, usePostEmailsSendEmail } from "openapi/api/endpoints"
import { toast } from "sonner"
import { useQueryClient } from "@tanstack/react-query"

interface PersonEmailDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    personId: string
    person: {
        name: string
        email: string
        position?: string
        company?: string
    }
}

export function PersonEmailDialog({ open, onOpenChange, personId, person }: PersonEmailDialogProps) {
    const { mutateAsync: sendEmail, isPending: isSending,isSuccess:emailSent } = usePostEmailsSendEmail()
    const queryClient = useQueryClient()
    const form = useForm({
        defaultValues: {
            to: "",
            subject: "",
            body: "",
        },
    })

    useEffect(() => {
        if (person?.email) {
            form.reset({
                to: person.email,
                subject: "",
                body: "",
            })
        }
    }, [person, form])




    const handleSendEmail = async () => {
        const formData = form.getValues()
        try {
            await sendEmail({
                data: {
                    first_name: person.name,
                    recipientEmail: formData.to,
                    subject: formData.subject,
                    message: formData.body,
                    contact_id: personId
                }
            })
            await queryClient.invalidateQueries({ queryKey: getGetEmailsQueryKey({ contact_id: personId }) })
            toast.success("Email sent successfully!")
        } catch {
            toast.error("Failed to send email. Please try again.")
            return
        }
    }

    const CONTROL = cn(FIELD_UNDERLINE, "pb-[7px] text-[12.5px] leading-[1.4]")

    return (
        <DialogShell
            open={open}
            onOpenChange={onOpenChange}
            wide
            title={`Send Email to ${person.name}`}
            footer={
                emailSent ? undefined : (
                    <>
                        <QuietAction size="md" onClick={() => onOpenChange(false)}>
                            Cancel
                        </QuietAction>
                        <PageAction
                            tone="primary"
                            onClick={handleSendEmail}
                            disabled={!form.watch("subject") || !form.watch("body") || isSending}
                        >
                            <Send className="size-[15px]" />
                            {isSending ? "Sending…" : "Send Email"}
                        </PageAction>
                    </>
                )
            }
        >
            {emailSent ? (
                <div className="flex flex-col items-center gap-3 py-8 text-center">
                    {/*
                      The app's `hired` green, not a literal `green-600` on a
                      `green-100` ground — those were picked against white and
                      the second of them is nearly invisible in dark.
                    */}
                    <span
                        className="flex size-12 items-center justify-center rounded-full"
                        style={{ backgroundColor: stageTint(stageHex("hired"), TINT.pill) }}
                    >
                        <CheckCircle className="size-6" style={{ color: stageHex("hired") }} />
                    </span>
                    <div>
                        <p className="text-[13px] font-medium">Email sent</p>
                        <p className="mt-1 text-[12px] leading-[1.5] text-muted-foreground">
                            It is on its way to {person.email}.
                        </p>
                    </div>
                </div>
            ) : (
                <>
                    <FieldShell label="To">
                        <input
                            id="email-to"
                            {...form.register("to")}
                            readOnly
                            aria-label="To"
                            className={cn(CONTROL, "cursor-not-allowed text-muted-foreground")}
                        />
                    </FieldShell>

                    <FieldShell label="Subject" className="mt-5">
                        <input
                            id="email-subject"
                            {...form.register("subject")}
                            placeholder="Email subject"
                            aria-label="Subject"
                            className={CONTROL}
                        />
                    </FieldShell>

                    <FieldShell label="Message" className="mt-5">
                        <textarea
                            id="email-body"
                            {...form.register("body")}
                            placeholder="Write your message here..."
                            rows={12}
                            aria-label="Message"
                            className={cn(FIELD_UNDERLINE, "resize-none pb-2.5 text-[12.5px] leading-[1.7]")}
                        />
                    </FieldShell>
                </>
            )}
        </DialogShell>
    )
}
